import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  CreditCard,
  ArrowDownToLine,
  ArrowUpFromLine,
  Calendar,
  Download,
  RefreshCcw,
  ChevronDown,
  TrendingUp,
  Clock,
  AlertTriangle,
  Zap,
  Timer,
} from 'lucide-react'
import { cn } from '@/lib/utils'

type DateRange = 'today' | 'yesterday' | 'week' | 'month'

export function TransactionsReportPage() {
  const [dateRange, setDateRange] = useState<DateRange>('today')

  const dateRangeLabels: Record<DateRange, string> = {
    today: 'วันนี้',
    yesterday: 'เมื่อวาน',
    week: '7 วันที่ผ่านมา',
    month: 'เดือนนี้',
  }

  // Mock data
  const summaryStats = {
    totalTransactions: 2458,
    depositCount: 1520,
    withdrawCount: 938,
    successRate: 98.5,
    avgProcessTime: 45, // seconds
    pendingCount: 24,
    totalAmount: 28100000,
  }

  const statusBreakdown = {
    deposit: {
      completed: 1480,
      pending: 25,
      failed: 15,
    },
    withdraw: {
      completed: 890,
      pending: 35,
      failed: 13,
    }
  }

  const processingTime = [
    { range: '< 30 วินาที', count: 1250, percent: 50.9 },
    { range: '30-60 วินาที', count: 850, percent: 34.6 },
    { range: '1-5 นาที', count: 280, percent: 11.4 },
    { range: '> 5 นาที', count: 78, percent: 3.1 },
  ]

  const hourlyVolume = [
    { hour: '00:00', deposit: 125, withdraw: 45, amount: 1250000 },
    { hour: '04:00', deposit: 68, withdraw: 25, amount: 680000 },
    { hour: '08:00', deposit: 185, withdraw: 95, amount: 2850000 },
    { hour: '12:00', deposit: 220, withdraw: 120, amount: 3500000 },
    { hour: '16:00', deposit: 310, withdraw: 180, amount: 4800000 },
    { hour: '20:00', deposit: 420, withdraw: 280, amount: 6500000 },
  ]

  const recentFailed = [
    { id: 'tx-001', type: 'withdraw', user: 'user123', amount: 50000, reason: 'ยอดเงินไม่เพียงพอ', time: '5 นาทีที่แล้ว' },
    { id: 'tx-002', type: 'deposit', user: 'user456', amount: 1000, reason: 'สลิปไม่ตรงกัน', time: '15 นาทีที่แล้ว' },
    { id: 'tx-003', type: 'withdraw', user: 'user789', amount: 100000, reason: 'เกินวงเงินต่อวัน', time: '30 นาทีที่แล้ว' },
  ]

  const topAmountRanges = [
    { range: '100 - 1,000', count: 580, percent: 23.6, totalAmount: 350000 },
    { range: '1,001 - 5,000', count: 720, percent: 29.3, totalAmount: 2160000 },
    { range: '5,001 - 10,000', count: 485, percent: 19.7, totalAmount: 3637500 },
    { range: '10,001 - 50,000', count: 450, percent: 18.3, totalAmount: 11250000 },
    { range: '50,001+', count: 223, percent: 9.1, totalAmount: 10702500 },
  ]

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('th-TH').format(num)
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('th-TH', {
      style: 'currency',
      currency: 'THB',
      minimumFractionDigits: 0,
    }).format(amount)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">รายงานธุรกรรม</h1>
          <p className="text-muted-foreground">วิเคราะห์ข้อมูลการฝาก-ถอนโดยละเอียด</p>
        </div>
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="gap-2">
                <Calendar className="h-4 w-4" />
                {dateRangeLabels[dateRange]}
                <ChevronDown className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {Object.entries(dateRangeLabels).map(([key, label]) => (
                <DropdownMenuItem key={key} onClick={() => setDateRange(key as DateRange)}>
                  {label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="outline" size="icon">
            <RefreshCcw className="h-4 w-4" />
          </Button>
          <Button variant="outline" className="gap-2">
            <Download className="h-4 w-4" />
            ส่งออก
          </Button>
        </div>
      </div>

      {/* Main Stats */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="p-3 rounded-lg bg-primary/10">
                <CreditCard className="h-6 w-6 text-primary" />
              </div>
            </div>
            <div className="mt-4">
              <p className="text-sm text-muted-foreground">ธุรกรรมทั้งหมด</p>
              <p className="text-2xl font-bold">{formatNumber(summaryStats.totalTransactions)}</p>
              <p className="text-xs text-muted-foreground mt-1">
                ยอดรวม {formatCurrency(summaryStats.totalAmount)}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="p-3 rounded-lg bg-win/10">
                <ArrowDownToLine className="h-6 w-6 text-win" />
              </div>
              <Badge variant="secondary" className="bg-win/10 text-win border-0">
                {formatNumber(summaryStats.depositCount)}
              </Badge>
            </div>
            <div className="mt-4">
              <p className="text-sm text-muted-foreground">รายการฝาก</p>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-xs text-win">✓ {statusBreakdown.deposit.completed}</span>
                <span className="text-xs text-warning">◷ {statusBreakdown.deposit.pending}</span>
                <span className="text-xs text-destructive">✕ {statusBreakdown.deposit.failed}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="p-3 rounded-lg bg-orange-500/10">
                <ArrowUpFromLine className="h-6 w-6 text-orange-500" />
              </div>
              <Badge variant="secondary" className="bg-orange-500/10 text-orange-500 border-0">
                {formatNumber(summaryStats.withdrawCount)}
              </Badge>
            </div>
            <div className="mt-4">
              <p className="text-sm text-muted-foreground">รายการถอน</p>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-xs text-win">✓ {statusBreakdown.withdraw.completed}</span>
                <span className="text-xs text-warning">◷ {statusBreakdown.withdraw.pending}</span>
                <span className="text-xs text-destructive">✕ {statusBreakdown.withdraw.failed}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="p-3 rounded-lg bg-blue-500/10">
                <Zap className="h-6 w-6 text-blue-500" />
              </div>
            </div>
            <div className="mt-4">
              <p className="text-sm text-muted-foreground">อัตราสำเร็จ</p>
              <p className="text-2xl font-bold text-win">{summaryStats.successRate}%</p>
              <p className="text-xs text-muted-foreground mt-1">
                เฉลี่ย {summaryStats.avgProcessTime} วินาที
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Processing Time & Volume */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Processing Time */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Timer className="h-5 w-5" />
              เวลาดำเนินการ
            </CardTitle>
            <CardDescription>การกระจายเวลาในการประมวลผลธุรกรรม</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {processingTime.map((item, idx) => (
                <div key={idx} className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium">{item.range}</span>
                    <span className="text-muted-foreground">{formatNumber(item.count)} รายการ</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Progress 
                      value={item.percent} 
                      className="h-2 flex-1"
                      indicatorClassName={cn(
                        idx === 0 && "bg-win",
                        idx === 1 && "bg-blue-500",
                        idx === 2 && "bg-warning",
                        idx === 3 && "bg-destructive"
                      )}
                    />
                    <span className="text-xs text-muted-foreground w-12 text-right">{item.percent}%</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Hourly Volume */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              ปริมาณธุรกรรมรายชั่วโมง
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {hourlyVolume.map((item, idx) => (
                <div key={idx} className="space-y-1">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground w-12">{item.hour}</span>
                    <div className="flex items-center gap-3">
                      <span className="text-win">↓{item.deposit}</span>
                      <span className="text-orange-500">↑{item.withdraw}</span>
                      <span className="font-medium">{formatCurrency(item.amount)}</span>
                    </div>
                  </div>
                  <div className="flex gap-0.5 h-2">
                    <div 
                      className="bg-win rounded-l-sm"
                      style={{ width: `${(item.deposit / (item.deposit + item.withdraw)) * 100}%` }}
                    />
                    <div 
                      className="bg-orange-500 rounded-r-sm"
                      style={{ width: `${(item.withdraw / (item.deposit + item.withdraw)) * 100}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Amount Ranges & Failed Transactions */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Amount Ranges */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              ช่วงจำนวนเงิน
            </CardTitle>
            <CardDescription>การกระจายธุรกรรมตามจำนวนเงิน</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {topAmountRanges.map((item, idx) => (
                <div key={idx} className="flex items-center justify-between py-2 border-b last:border-0">
                  <div className="flex items-center gap-3">
                    <div className="w-20 text-sm font-medium">฿{item.range}</div>
                    <Progress value={item.percent} className="h-2 w-24" />
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium">{formatNumber(item.count)} รายการ</p>
                    <p className="text-xs text-muted-foreground">{formatCurrency(item.totalAmount)}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Recent Failed */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              ธุรกรรมล้มเหลวล่าสุด
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {recentFailed.map((tx, idx) => (
                <div key={idx} className="flex items-start gap-3 p-3 rounded-lg bg-destructive/5 border border-destructive/20">
                  <div className={cn(
                    "p-2 rounded-lg",
                    tx.type === 'deposit' ? "bg-win/10" : "bg-orange-500/10"
                  )}>
                    {tx.type === 'deposit' ? (
                      <ArrowDownToLine className="h-4 w-4 text-win" />
                    ) : (
                      <ArrowUpFromLine className="h-4 w-4 text-orange-500" />
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-sm">{tx.user}</span>
                      <span className="font-semibold text-destructive">{formatCurrency(tx.amount)}</span>
                    </div>
                    <p className="text-xs text-destructive mt-1">{tx.reason}</p>
                    <p className="text-xs text-muted-foreground mt-1">{tx.time}</p>
                  </div>
                </div>
              ))}
            </div>
            <Button variant="outline" className="w-full mt-4" size="sm">
              ดูทั้งหมด
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}








